import React, { useEffect, useMemo, useState } from 'react';
import { fetchProductAlertConfig, fetchProductAlertTypes, fetchProductNamePick } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Product Alert Configurations: which products stamp an alert on an order, and the
 * alert types (Azure exit WS8). Reads sql01.ProductAlertConfig and ProductAlertTypes. Saving is off
 * until ws8_product_alert_config_live. A setting cannot be edited, only deleted and added again.
 */

const showDate = (ymd) => { if (!ymd) return ''; const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };

export default function ProductAlertConfig() {
  const live = useWriteLive('ws8_product_alert_config');
  const [products, setProducts] = useState([]);
  const [find, setFind] = useState('');
  const [adding, setAdding] = useState(null);
  const [type, setType] = useState(null);
  const config = useRows(() => fetchProductAlertConfig(), []);
  const types = useRows(() => fetchProductAlertTypes(), []);
  const saveConfig = useSave(config.reload);
  const saveType = useSave(() => { types.reload(); config.reload(); });

  useEffect(() => { fetchProductNamePick().then(setProducts).catch(() => {}); }, []);
  const matches = useMemo(() => {
    const q = find.trim().toLowerCase();
    return (q ? products.filter((p) => String(p.label).toLowerCase().includes(q)) : products).slice(0, 200);
  }, [find, products]);

  const add = async () => {
    if (!adding.catalogId || !adding.alertType) return saveConfig.setError('Pick a product and an alert type.');
    if (await saveConfig.save('product_alert_config_add', adding)) setAdding(null);
    return null;
  };
  const submitType = async () => {
    if (!type.name.trim()) return saveType.setError('Enter a name.');
    if (await saveType.save('product_alert_type_save', type)) setType(null);
    return null;
  };
  const confirmThen = (fn) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Do you want to delete?')) fn();
  };

  return (
    <div>
      <PageTitle>Product Alert Configurations</PageTitle>
      <WriteOffNote live={live} what="Adding and deleting" />
      <div className="port-filters">
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => { saveConfig.setError(null); setFind(''); setAdding({ catalogId: '', alertType: '', isActive: true }); }}>Add Product Alert Configuration</button>
        <button type="button" className="btn btn-outline" disabled={!live} onClick={() => { saveType.setError(null); setType({ id: 0, name: '' }); }}>Add Alert Type</button>
      </div>
      <ListCard title="Configurations" rows={config.rows} error={config.error} empty="No configurations.">
        <table className="trans-table">
          <thead><tr><th>Product</th><th>Alert Type</th><th>Active</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(config.rows || []).map((r) => (
              <tr key={r.id}>
                <td>{r.productName}</td>
                <td>{r.alertType}</td>
                <td>{r.isActive ? 'Active' : 'Not Active'}</td>
                <td className="staff-actions">
                  <button type="button" className="btn btn-ghost" disabled={!live || saveConfig.saving} onClick={() => confirmThen(() => saveConfig.save('product_alert_config_delete', { id: r.id }))}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {saveConfig.error && !adding && <div className="staff-msg staff-msg-error">{saveConfig.error}</div>}
      <div className="staff-section">
        <ListCard title="Alert types" rows={types.rows} error={types.error} empty="No alert types.">
          <table className="trans-table">
            <thead><tr><th>Name</th><th>Created</th><th>Updated</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {(types.rows || []).map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{showDate(r.createdAt)}</td>
                  <td>{showDate(r.updatedAt)}</td>
                  <td className="staff-actions">
                    <button type="button" className="btn btn-ghost" disabled={!live || saveType.saving} onClick={() => { saveType.setError(null); setType({ id: r.id, name: r.name }); }}>Edit</button>
                    <button type="button" className="btn btn-ghost" disabled={!live || saveType.saving} onClick={() => confirmThen(() => saveType.save('product_alert_type_delete', { id: r.id }))}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ListCard>
      </div>
      {saveType.error && !type && <div className="staff-msg staff-msg-error">{saveType.error}</div>}
      {adding && (
        <EditDialog title="Add Product Alert Configuration" live={live} saving={saveConfig.saving} error={saveConfig.error} onClose={() => setAdding(null)} onSave={add}>
          <Field label="Find a product"><input className="form-input" value={find} onChange={(e) => setFind(e.target.value)} /></Field>
          <Field label="Product">
            <select className="form-select" value={adding.catalogId} onChange={(e) => setAdding({ ...adding, catalogId: e.target.value })}>
              <option value="">Select a product</option>
              {matches.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Alert Type">
            <select className="form-select" value={adding.alertType} onChange={(e) => setAdding({ ...adding, alertType: e.target.value })}>
              <option value="">Select a type</option>
              {(types.rows || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Active">
            <select className="form-select" value={adding.isActive ? '1' : '0'} onChange={(e) => setAdding({ ...adding, isActive: e.target.value === '1' })}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </Field>
        </EditDialog>
      )}
      {type && (
        <EditDialog title={type.id ? 'Edit Alert Type' : 'Add Alert Type'} live={live} saving={saveType.saving} error={saveType.error} onClose={() => setType(null)} onSave={submitType}>
          <Field label="Name"><input className="form-input" maxLength={255} value={type.name} onChange={(e) => setType({ ...type, name: e.target.value })} /></Field>
        </EditDialog>
      )}
    </div>
  );
}
